import { cn } from '@immo/ui-kit';
import { ActionsMenu, type ActionsMenuEntry } from '../menu/actions-menu';
import { useTranslations } from '../use-translations';

type Props = {
  /** The header the member is looking at — used verbatim in the trigger's aria-label. */
  readonly columnLabel: string;
  readonly canMoveLeft: boolean;
  readonly canMoveRight: boolean;
  readonly onMove: (direction: 'left' | 'right') => void;
  /** `null` for a column with no sort field: the two sort items and the separator are absent. */
  readonly onSort: ((direction: 'asc' | 'desc') => void) | null;
  /** `null` for the always-visible column: the item is ABSENT, never disabled. */
  readonly onHide: (() => void) | null;
  readonly onOpenPanel: () => void;
  readonly className?: string;
};

const TRIGGER_CLASS_NAME =
  'h-6 w-6 opacity-0 group-hover/col:opacity-100 focus-visible:opacity-100 aria-expanded:opacity-100';

export function ColumnHeaderMenu({
  columnLabel,
  canMoveLeft,
  canMoveRight,
  onMove,
  onSort,
  onHide,
  onOpenPanel,
  className,
}: Props) {
  const t = useTranslations('components.column-preference.menu');

  // Not memoized: `onMove`/`onSort`/`onHide`/`onOpenPanel` are fresh closures on every
  // parent render, so a useMemo here would recompute every time anyway.
  const items: ReadonlyArray<ActionsMenuEntry> = [
    ...(onSort === null
      ? []
      : [
          { key: 'sort-asc', label: t('sort-asc'), onSelect: () => onSort('asc') },
          { key: 'sort-desc', label: t('sort-desc'), onSelect: () => onSort('desc') },
          { key: 'sort-separator', separator: true as const },
        ]),
    { key: 'move-left', label: t('move-left'), disabled: !canMoveLeft, onSelect: () => onMove('left') },
    { key: 'move-right', label: t('move-right'), disabled: !canMoveRight, onSelect: () => onMove('right') },
    { key: 'hide-separator', separator: true as const },
    ...(onHide === null ? [] : [{ key: 'hide', label: t('hide'), onSelect: onHide }]),
    { key: 'all', label: t('all'), onSelect: onOpenPanel },
  ];

  return (
    <span className={cn('inline-flex', className)}>
      <ActionsMenu
        items={items}
        triggerLabel={t('trigger', { column: columnLabel })}
        triggerClassName={TRIGGER_CLASS_NAME}
      />
    </span>
  );
}
